import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type AlertType = 'warning' | 'error' | 'info' | 'success';

interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  type?: AlertType;
  title?: string;
  message?: string;
  messages?: string[];
  confirmText?: string;
  onConfirm?: () => void;
  showCancel?: boolean;
  cancelText?: string;
  autoClose?: number;
}

const icons: Record<AlertType, string> = {
  warning: '⚠️',
  error: '🚫',
  info: 'ℹ️',
  success: '✅',
};

const gradients: Record<AlertType, string> = {
  warning: 'from-amber-400 to-orange-500',
  error: 'from-red-400 to-rose-600',
  info: 'from-blue-400 to-indigo-600',
  success: 'from-green-400 to-emerald-600',
};

const bgTints: Record<AlertType, string> = {
  warning: 'bg-amber-50',
  error: 'bg-red-50',
  info: 'bg-blue-50',
  success: 'bg-green-50',
};

const textColors: Record<AlertType, string> = {
  warning: 'text-amber-700',
  error: 'text-red-700',
  info: 'text-blue-700',
  success: 'text-green-700',
};

const btnColors: Record<AlertType, string> = {
  warning: 'bg-amber-500 hover:bg-amber-600',
  error: 'bg-red-500 hover:bg-red-600',
  info: 'bg-blue-500 hover:bg-blue-600',
  success: 'bg-green-500 hover:bg-green-600',
};

const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  onClose,
  type = 'warning',
  title,
  message,
  messages,
  confirmText = 'Entendi',
  onConfirm,
  showCancel = false,
  cancelText = 'Cancelar',
  autoClose,
}) => {
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen && autoClose) {
      setCountdown(Math.ceil(autoClose / 1000));
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev && prev <= 1) {
            clearInterval(interval);
            onClose();
            return null;
          }
          return prev ? prev - 1 : null;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isOpen, autoClose, onClose]);

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ scale: 0.7, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.7, opacity: 0, y: 30 }}
            transition={{ type: 'spring', damping: 18, stiffness: 300 }}
            className={`relative ${bgTints[type]} rounded-2xl shadow-2xl w-full max-w-md overflow-hidden`}
          >
            {/* Top gradient bar */}
            <div className={`h-2 bg-gradient-to-r ${gradients[type]}`} />

            {/* Animated icon */}
            <div className="flex flex-col items-center pt-6 pb-2">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.1 }}
                className={`text-5xl mb-2`}
              >
                {icons[type]}
              </motion.div>
              {countdown !== null && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`text-xs ${textColors[type]} font-medium mb-1`}
                >
                  Redirecionando em {countdown}s...
                </motion.div>
              )}
            </div>

            {/* Content */}
            <div className="px-6 pb-6 text-center">
              {title && (
                <h3 className={`text-lg font-bold ${textColors[type]} mb-3`}>
                  {title}
                </h3>
              )}
              {message && (
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  {message}
                </p>
              )}
              {messages && messages.length > 0 && (
                <div className="space-y-2 mb-4">
                  {messages.map((msg, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + idx * 0.1 }}
                      className={`flex items-start gap-2 text-left ${bgTints[type]} rounded-lg p-3 border border-current/10`}
                    >
                      <span className="text-lg flex-shrink-0">{icons[type]}</span>
                      <p className="text-sm text-gray-700 leading-relaxed">{msg}</p>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 justify-center mt-4">
                {showCancel && (
                  <button
                    onClick={handleClose}
                    className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    {cancelText}
                  </button>
                )}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleConfirm}
                  className={`px-6 py-2.5 text-sm font-bold text-white rounded-lg shadow-md transition-colors ${btnColors[type]}`}
                >
                  {confirmText}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AlertModal;
